import { useEffect, useState } from "react";
import { api, useAuth } from "../../App";
import { mono, Chip, Btn, Select, Input, Textarea, Field, PageTitle, Drawer } from "./ptUi";

// Risks & blockers — production risks/escalations. Writes pt_risks in DM's Mongo.
// Resolving auto-stamps the date. Lead-managed.

const AREAS = ["Client", "Casting", "Equipment", "Studio", "Crew", "Budget", "Delivery", "Other"];
const SEV = ["High", "Medium", "Low"];
const STATUS = ["Open", "In Progress", "Resolved"];

export default function PtrackerRisks() {
  const { user } = useAuth();
  const isLead = ["admin", "srmanager", "manager", "creativedirector", "associatecreativedirector"].includes((user?.role || "").toLowerCase());
  const [risks, setRisks] = useState([]);
  const [members, setMembers] = useState([]);
  const [lists, setLists] = useState({ brands: [] });
  const [statusF, setStatusF] = useState("active");
  const [sevF, setSevF] = useState("");
  const [drawer, setDrawer] = useState(null);
  const [err, setErr] = useState("");

  const load = () => api.get("/ptracker/risks").then((r) => setRisks(r.data.risks || [])).catch((e) => setErr(e.response?.data?.detail || "Couldn't load risks."));
  useEffect(() => { load(); api.get("/ptracker/refs/members").then((r) => setMembers(r.data.members || [])).catch(() => {}); api.get("/ptracker/refs/lists").then((r) => setLists(r.data)).catch(() => {}); }, []);

  const rows = risks.filter((r) =>
    (statusF === "all" ? true : statusF === "active" ? r.status !== "Resolved" : r.status === statusF) &&
    (!sevF || r.severity === sevF));

  const setStatus = (r, v) => api.put(`/ptracker/risks/${r.id}`, { status: v }).then(load).catch((e) => alert(e.response?.data?.detail || "Failed"));
  const th = { textAlign: "left", fontFamily: mono, fontSize: 10, fontWeight: 700, color: "var(--t3)", letterSpacing: ".08em", textTransform: "uppercase", padding: "8px 10px" };
  const td = { padding: "8px 10px", fontSize: 13, color: "var(--t1)", verticalAlign: "middle" };
  const sevEdge = (s) => s === "High" ? "var(--r)" : s === "Medium" ? "#d97706" : "var(--bdr)";

  return (
    <div style={{ padding: "24px 28px", maxWidth: 1200, margin: "0 auto" }} data-testid="ptracker-risks">
      <PageTitle title="Risks & Blockers" sub="Escalations and production risks. Resolving auto-stamps the date."
        right={isLead ? <Btn onClick={() => setDrawer({ date_raised: new Date().toISOString().slice(0, 10), severity: "Medium", status: "Open", owner_id: user?.user_id || user?.id })}>+ Raise risk</Btn> : null} />

      <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
        <Select value={statusF} onChange={(e) => setStatusF(e.target.value)} options={[{ value: "active", label: "Active (Open + In Progress)" }, ...STATUS, { value: "all", label: "All" }]} />
        <Select value={sevF} onChange={(e) => setSevF(e.target.value)} options={SEV} allowEmpty emptyLabel="All severities" />
      </div>

      {err && <div style={{ color: "var(--r)", fontFamily: mono, fontSize: 12, marginBottom: 10 }}>{err}</div>}

      <div style={{ background: "var(--s1)", border: "1px solid var(--bdr)", borderRadius: "var(--radius-sm)", overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead style={{ background: "var(--s2)" }}>
            <tr><th style={th}>Raised</th><th style={th}>Area</th><th style={th}>Brand</th><th style={th}>Risk</th><th style={th}>Severity</th><th style={th}>Owner</th><th style={th}>Status</th><th style={th}>Escalated</th></tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} style={{ borderTop: "1px solid var(--bdr)", borderLeft: `3px solid ${sevEdge(r.severity)}`, cursor: isLead ? "pointer" : "default", opacity: r.status === "Resolved" ? .6 : 1 }}
                onClick={() => isLead && setDrawer(r)}>
                <td style={{ ...td, fontFamily: mono, fontSize: 11, color: "var(--t3)" }}>{r.date_raised}</td>
                <td style={{ ...td, color: "var(--t2)" }}>{r.area}</td>
                <td style={{ ...td, color: "var(--t2)" }}>{r.brand_project || "—"}</td>
                <td style={{ ...td, maxWidth: 320 }}>{r.description}</td>
                <td style={td} onClick={(e) => e.stopPropagation()}><Chip value={r.severity} /></td>
                <td style={{ ...td, color: "var(--t2)" }}>{r.owner_name || "—"}</td>
                <td style={td} onClick={(e) => e.stopPropagation()}><Chip value={r.status} options={isLead ? STATUS : null} onChange={(v) => setStatus(r, v)} /></td>
                <td style={{ ...td, fontFamily: mono, fontSize: 11, color: "var(--t3)" }}>{r.escalated_to || "—"}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={8} style={{ padding: 40, textAlign: "center", fontFamily: mono, fontSize: 13, color: "var(--t3)" }}>No risks in this view.</td></tr>}
          </tbody>
        </table>
      </div>

      {drawer && <RiskForm risk={drawer} members={members} lists={lists} me={user} onClose={() => setDrawer(null)} onSaved={() => { setDrawer(null); load(); }} />}
    </div>
  );
}

function RiskForm({ risk, members, lists, me, onClose, onSaved }) {
  const [f, setF] = useState({
    date_raised: risk.date_raised || new Date().toISOString().slice(0, 10), area: risk.area || "", brand_project: risk.brand_project || "",
    description: risk.description || "", severity: risk.severity || "Medium", owner_id: risk.owner_id || (me?.user_id || me?.id),
    mitigation: risk.mitigation || "", status: risk.status || "Open", escalated_to: risk.escalated_to || "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const editing = !!risk.id;
  const save = () => {
    if (!f.description.trim() || !f.area) return alert("Area and description are required.");
    setSaving(true);
    const req = editing ? api.put(`/ptracker/risks/${risk.id}`, f) : api.post("/ptracker/risks", f);
    req.then(onSaved).catch((e) => { alert(e.response?.data?.detail || "Save failed"); setSaving(false); });
  };
  const del = () => { if (window.confirm("Delete this risk?")) api.delete(`/ptracker/risks/${risk.id}`).then(onSaved).catch((e) => alert(e.response?.data?.detail || "Failed")); };
  return (
    <Drawer title={editing ? "Edit risk" : "Raise a risk"} onClose={onClose}>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Field label="Date raised"><Input type="date" value={f.date_raised} onChange={(e) => set("date_raised", e.target.value)} /></Field>
          <Field label="Area"><Select options={AREAS} value={f.area} onChange={(e) => set("area", e.target.value)} allowEmpty /></Field>
          <Field label="Brand / Project"><Select options={lists.brands} value={f.brand_project} onChange={(e) => set("brand_project", e.target.value)} allowEmpty /></Field>
          <Field label="Severity"><Select options={["High", "Medium", "Low"]} value={f.severity} onChange={(e) => set("severity", e.target.value)} /></Field>
        </div>
        <Field label="Risk / blocker"><Textarea rows={2} value={f.description} onChange={(e) => set("description", e.target.value)} /></Field>
        <Field label="Mitigation / action"><Textarea rows={2} value={f.mitigation} onChange={(e) => set("mitigation", e.target.value)} /></Field>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Field label="Owner"><Select options={members.map((m) => ({ value: m.id, label: m.name }))} value={f.owner_id} onChange={(e) => set("owner_id", e.target.value)} allowEmpty /></Field>
          <Field label="Status"><Select options={["Open", "In Progress", "Resolved"]} value={f.status} onChange={(e) => set("status", e.target.value)} /></Field>
        </div>
        <Field label="Escalated to"><Input value={f.escalated_to} onChange={(e) => set("escalated_to", e.target.value)} placeholder="e.g. AP, Client lead" /></Field>
        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6 }}>
          <div>{editing && <Btn kind="danger" onClick={del}>Delete</Btn>}</div>
          <div style={{ display: "flex", gap: 8 }}><Btn kind="ghost" onClick={onClose}>Cancel</Btn><Btn onClick={save} disabled={saving}>{saving ? "Saving…" : "Save"}</Btn></div>
        </div>
      </div>
    </Drawer>
  );
}
